import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { UserInfo } from '@cobu/types';
import { Strings } from '../constants';
import { useAcceptTermsMutation, useUpdateUserMutation } from './userHooks';
import { RootState } from '../store';
import { setPendingContactToken } from '../store/authSlice';
import { logger } from '../utils/logger';

// Handshake-created residents get this placeholder until they accept the terms
// (ExternalAuthService._createExternalUser in emperor).
const PENDING_CONSENT_EMAIL = /^pending-consent\+.+@cobu\.invalid$/;
const isPendingConsentEmail = (email?: string | null) => !!email && PENDING_CONSENT_EMAIL.test(email);

/**
 * Drives the single welcome-and-consent screen.
 *
 * There is no navigation on success: the screen is a gate rendered in place of the
 * router, so once `acceptedTermsAndConditions` flips in the store the feed renders
 * underneath it — and a resident who arrived on a deep link keeps that destination.
 */
export function useOnboarding() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const pendingContactToken = useSelector((state: RootState) => state.auth.pendingContactToken);
  const acceptTermsMutation = useAcceptTermsMutation();
  const updateUserMutation = useUpdateUserMutation();
  const isPending = acceptTermsMutation.isPending || updateUserMutation.isPending;

  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
  const [hasConsentedToSMS, setHasConsentedToSMS] = useState(user?.optedInSMSMessages ?? false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openTermsModal = useCallback(() => {
    /* ANALYTICS_DISABLED: DID_OPEN_CODE_OF_CONDUCT ('did_open_code_of_conduct')
      analyticsService.trackEvent(AnalyticsEvent.DID_OPEN_CODE_OF_CONDUCT);
      */
    setIsTermsModalOpen(true);
  }, []);

  // Checkbox state lives here rather than in the modal, so dismissing the modal
  // cannot disturb what the resident has already ticked.
  const closeTermsModal = useCallback(() => setIsTermsModalOpen(false), []);

  const join = useCallback(async () => {
    if (!hasAgreedToTerms || isPending) return;

    const strings = Strings.get();

    if (!user?.uuid) {
      setError(strings.common.error.userNotFound);
      return;
    }

    try {
      setError(null);
      if (pendingContactToken) {
        // Accepts the terms and saves the email/phone from the token in one write. A
        // rejected token (CONTACT_TOKEN_INVALID) accepts nothing; reopening the app
        // gets a fresh one.
        await acceptTermsMutation.mutateAsync({
          pendingContactToken,
          // The code of conduct is folded into the first checkbox now that the separate
          // pledge screen is gone; keep the legacy flag truthful for the other clients.
          agreedToCoC: true,
          optedInSMSMessages: hasConsentedToSMS,
        });
      } else {
        // No token: a backend that predates accept-terms, which also still stores the
        // real email/phone at creation — so the plain PATCH is all that's needed.
        // A placeholder email without a token means the contact details can't be saved;
        // accepting anyway would strand the resident on it, so refuse — reopening the
        // app runs a fresh handshake that issues a token.
        if (isPendingConsentEmail(user.email)) {
          setError(strings.onboarding.welcome.error);
          return;
        }
        const consentedAt = new Date().toISOString();
        const userData: Partial<UserInfo> = {
          acceptedTermsAndConditions: true,
          acceptedTermsAndConditionsDate: consentedAt,
          agreedToCoC: true,
          optedInSMSMessages: hasConsentedToSMS,
          ...(hasConsentedToSMS ? { optedInSMSMessagesAt: consentedAt } : {}),
        };
        await updateUserMutation.mutateAsync({ userId: user.uuid, userData });
      }
      analyticsService.trackEvent(AnalyticsEvent.DID_COMPLETE_PLEDGE);
    } catch (mutationError) {
      // Status and code only: an axios error carries the request body (the token,
      // i.e. the resident's email/phone) in config.data.
      const response = (mutationError as { response?: { status?: number; data?: { code?: string } } })?.response;
      logger.error('Failed to save onboarding consent:', { status: response?.status, code: response?.data?.code });
      setError(strings.onboarding.welcome.error);
      // Must return: carrying on would clear the token and boot Intercom without
      // consent, and a retry would fall into the no-token branch.
      return;
    }

    dispatch(setPendingContactToken(null));
  }, [
    hasAgreedToTerms,
    hasConsentedToSMS,
    isPending,
    user?.uuid,
    pendingContactToken,
    acceptTermsMutation,
    updateUserMutation,
    dispatch,
  ]);

  return {
    hasAgreedToTerms,
    setHasAgreedToTerms,
    hasConsentedToSMS,
    setHasConsentedToSMS,
    isTermsModalOpen,
    openTermsModal,
    closeTermsModal,
    join,
    isPending,
    error,
  };
}
